import { useEffect, useRef, useState, RefObject } from 'react';

/**
 * Hook to detect if content is visually truncated by a CSS line clamp
 * @param text - The text content to measure
 * @param clampClass - The CSS class that applies line clamping (e.g., 'line-clamp-4')
 * @param elementRef - Reference to the element being measured for truncation
 * @returns boolean indicating if content is truncated
 */
export function useIsTruncated(
  text: string | undefined,
  clampClass: string,
  elementRef: RefObject<HTMLElement>
): boolean {
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (!text || !elementRef.current) {
      setIsTruncated(false);
      return;
    }

    // Create a temporary element to measure the full height without clamping
    const tempElement = document.createElement('div');
    tempElement.style.position = 'absolute';
    tempElement.style.visibility = 'hidden';
    tempElement.style.pointerEvents = 'none';

    // Copy relevant styles from the original element
    const styles = window.getComputedStyle(elementRef.current);
    tempElement.style.width = styles.width;
    tempElement.style.fontSize = styles.fontSize;
    tempElement.style.lineHeight = styles.lineHeight;
    tempElement.style.whiteSpace = styles.whiteSpace;
    tempElement.style.wordWrap = styles.wordWrap;
    tempElement.style.fontFamily = styles.fontFamily;
    tempElement.style.fontWeight = styles.fontWeight;
    tempElement.style.padding = styles.padding;
    tempElement.style.margin = '0';

    // Set the text content
    tempElement.textContent = text;

    // Append to document to measure
    document.body.appendChild(tempElement);
    const fullHeight = tempElement.offsetHeight;
    document.body.removeChild(tempElement);

    // Get the clamped height from the actual element
    const clampedHeight = elementRef.current.offsetHeight;

    // Content is truncated if the clamped height is less than the full height
    setIsTruncated(clampedHeight < fullHeight);
  }, [text, clampClass, elementRef]);

  return isTruncated;
}
